import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef, NbDialogService, NbMenuItem, NbMenuService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subject, filter, takeUntil } from 'rxjs';
import { ConfirmDialogComponent } from '../../components/dialogs/confirm-dialog/confirm-dialog.component';
import {
  AssignmentRule,
  DerivablePreview,
  CurriculumItem,
  CurriculumItemInput,
  CurriculumAttribute,
  CurriculumService,
  CurriculumTree,
  CurriculumTreeNode,
  LearningGoal,
  MoodleCategoryGroup,
  MoodleCourseOption,
  StudyProgram,
} from '../../services/curriculum.service';

interface CurriculumTreeRow {
  kind: 'group' | 'course';
  id: string;
  depth: number;
  ancestors: string[]; // group ids that must all be expanded for this row to show
  name: string;
  group?: CurriculumTreeNode;
  item?: CurriculumItem;
  /**
   * Guide lines to the left of this row, one entry per ANCESTOR level
   * (length = depth - 1). `true` = that ancestor still has siblings below, so
   * its branch line passes through this row; `false` = the branch ended above
   * and the band stays empty.
   *
   * The row's own connector band (the last one) is not in here — it is drawn
   * from `isLast`, which decides between `├` and `└`.
   */
  guides: boolean[];
  /** Last child of its parent → the connector stops at the row's middle. */
  isLast: boolean;
}

/**
 * Admin page for curriculum management:
 * - Left: list of study programs (create/edit/delete)
 * - Right: curriculum items of the selected program (course x semester x goals)
 *
 * Learner enrollment is handled from the learner profile dialog — this page
 * only defines the structure.
 */
@Component({
  selector: 'app-curriculum',
  templateUrl: './curriculum.component.html',
  styleUrl: './curriculum.component.scss',
})
export class CurriculumComponent implements OnInit, OnDestroy {
  @ViewChild('createDialog') createDialog!: TemplateRef<any>;
  private createDialogRef?: NbDialogRef<any>;

  selectedProgramId: string | null = null;
  activeTab: 'kurse' | 'struktur' | 'regeln' = 'kurse';

  // "Curriculum anlegen" flow
  createMode: 'categories' | 'manual' | 'import' | null = null;
  moodleCourses: MoodleCategoryGroup[] = [];
  selectedCategoryKeys: string[] = [];
  categoryProgramName = '';
  categoryProgramCode = '';
  // Bulk-adoption: mark category nodes in a tree as study-program roots
  catExpanded = new Set<string>();
  selectedProgramPrefixes = new Set<string>();
  bulkCreateRules = true;

  programForm: FormGroup;
  itemForm: FormGroup;
  groupForm: FormGroup;
  ruleForm: FormGroup;

  tree: CurriculumTree | null = null;
  rules: AssignmentRule[] = [];

  // Tree-table state
  treeRows: CurriculumTreeRow[] = [];
  expanded = new Set<string>();
  goalsOpen = new Set<string>();
  goalDrafts: Record<string, LearningGoal[]> = {};
  descriptionDrafts: Record<string, string> = {};
  /** Where the drafted content came from, so an adoption is recorded as such
   *  and a later resync knows what it may touch. */
  sourceDrafts: Record<string, { description: string; goals: string }> = {};
  proposalLoading: Record<string, boolean> = {};

  /** Multi-select for bulk grouping. Courses only — a group cannot be moved
   *  into a group this way (that is what the tree's move actions are for). */
  selectedItemIds = new Set<string>();
  bulkTargetGroupId: string | null = null;

  /** Which level of the Moodle category path is a module. Asked rather than
   *  guessed: one customer keeps modules on level 2, the next on level 3. */
  categoryModuleDepth: number | null = null;

  /** Inline rename in the program list, opened from the row menu. */
  renamingProgramId: string | null = null;
  renameProgramName = '';

  /** Row that just moved — drives the highlight. A reorder is two numbers
   *  changing places on the server; without a visible trace the row simply
   *  appears somewhere else and the eye does not follow it. */
  justMovedRowId: string | null = null;
  private justMovedTimer?: ReturnType<typeof setTimeout>;

  // Option lists for <nb-select> are MEMOISED, not recomputed per binding.
  // A getter that builds a fresh array on every change-detection run makes
  // nb-select rebuild its options, which schedules another run — the page
  // then spins instead of rendering. That is what froze the app when the
  // Zuweisungsregeln panel opened.
  private courseOptionsMemo: { sig: string; value: { course_id: string; label: string }[] } | null = null;
  private groupOptionsMemo: { sig: string; value: { id: string; label: string }[] } | null = null;
  private ruleRefOptionsMemo: { sig: string; value: { key: string; label: string }[] } | null = null;

  private readonly programMenuPrefix = 'curriculumProgramMenu-';
  private readonly rowMenuPrefix = 'curriculumRowMenu-';
  /** Cached per row and invalidated by a signature: the context-menu
   *  directive re-initialises whenever the array identity changes, so a fresh
   *  array per change-detection run would tear an open menu down. */
  private rowMenuCache = new Map<string, { sig: string; items: NbMenuItem[] }>();
  /** Cached per program: NbContextMenuDirective re-initialises whenever the
   *  array identity changes, so a fresh array per change-detection run would
   *  tear the menu down while it is open. */
  private programMenuCache = new Map<string, NbMenuItem[]>();
  private readonly destroy$ = new Subject<void>();
  readonly moduleDepthOptions = [1, 2, 3, 4];
  addCourseForGroup: string | null = null;
  newCourseId: string | null = null;
  showRules = false;

  // Inline edit state
  editGroupId: string | null = null;
  editGroupName = '';
  editGroupType = '';
  /** "the order of the courses in this group is a sequence", not just a sort. */
  editGroupOrdered = false;
  editItemId: string | null = null;
  editItemSemester = 1;
  editItemMandatory = true;
  editItemAttrs: Record<string, any> = {};

  // Custom attributes (per-program columns)
  attributes: CurriculumAttribute[] = [];
  showAttrManager = false;

  // Pagination + column visibility
  treePage = 0;
  treePageSize = 50;
  readonly pageSizeOptions = [25, 50, 100];
  colHidden = new Set<string>(); // 'type' | 'sem' | 'goals' | 'attr:<id>'
  newAttrLabel = '';
  newAttrType: 'text' | 'number' | 'dropdown' | 'boolean' = 'text';
  newAttrOptions = '';
  newAttrRequired = false;

  // Import state
  importFile: File | null = null;
  importDragOver = false;
  importFileError: string | null = null;
  importJobId: string | null = null;
  importStatus: 'idle' | 'queued' | 'processing' | 'done' | 'error' = 'idle';
  importParsed: any = null;
  importWarnings: string[] = [];
  importError: string | null = null;
  private importPoll: any = null;

  readonly BLOOM_LEVELS = [
    'remember',
    'understand',
    'apply',
    'analyze',
    'evaluate',
    'create',
  ];
  readonly RULE_TYPES = ['moodle_category', 'course_bucket', 'manual'];
  readonly GROUP_TYPES = ['semester', 'jahrgang', 'lernfeld', 'modul', 'fachgebiet', 'sonstiges'];

  constructor(
    protected fb: FormBuilder,
    public curriculumService: CurriculumService,
    protected toastr: NbToastrService,
    protected dialogService: NbDialogService,
    protected translate: TranslateService,
    protected nbMenuService: NbMenuService
  ) {
    this.programForm = this.fb.group({
      name: ['', Validators.required],
      code: [''],
      description: [''],
    });
    this.itemForm = this.fb.group({
      course_id: ['', Validators.required],
      semester: [1, [Validators.required, Validators.min(1), Validators.max(20)]],
      is_mandatory: [true],
      learning_goals: this.fb.array([]),
    });
    this.groupForm = this.fb.group({
      name: ['', Validators.required],
      group_type: [''],
      source_type: ['manual'],
      source_ref: [''],
    });
    this.ruleForm = this.fb.group({
      rule_type: ['moodle_category', Validators.required],
      ref_key: [''],
      default_start_semester: [1],
    });
  }

  get learningGoals(): FormArray {
    return this.itemForm.get('learning_goals') as FormArray;
  }

  ngOnInit(): void {
    this.curriculumService.listPrograms().subscribe();
    this.subscribeProgramMenu();
    this.subscribeRowMenu();
    // Course basis is the Moodle canonical course list (mapped to course_id),
    // NOT the al-filemanager /courses endpoint.
    this.loadMoodleCourses();
  }

  loadMoodleCourses() {
    this.curriculumService.listMoodleCourses().subscribe({
      next: (cats) => (this.moodleCourses = cats),
      error: () => (this.moodleCourses = []),
    });
  }

  /** Flat list of pickable courses (only those already mirrored → have course_id). */
  get courseOptions(): { course_id: string; label: string }[] {
    const sig = `${this.moodleCourses.length}|${this.moodleCourses
      .map((c) => `${c.key}:${c.courses.length}`)
      .join(',')}`;
    if (this.courseOptionsMemo?.sig === sig) return this.courseOptionsMemo.value;
    const out: { course_id: string; label: string }[] = [];
    for (const cat of this.moodleCourses) {
      for (const c of cat.courses) {
        if (c.course_id) {
          out.push({ course_id: c.course_id, label: `${cat.label} · ${c.name}` });
        }
      }
    }
    this.courseOptionsMemo = { sig, value: out };
    return out;
  }

  setCreateMode(mode: 'categories' | 'manual' | 'import' | null) {
    this.createMode = this.createMode === mode ? null : mode;
    if (this.createMode === 'categories' && this.moodleCourses.length === 0) {
      this.loadMoodleCourses();
    }
  }

  /** Open the "Curriculum erstellen" dialog. Mirrors the other create flows
   *  (bucket-dialog etc.): a header button opens a dialog that owns the form,
   *  rather than an inline panel in the left column. The mode chooser lives
   *  inside the dialog. */
  openCreate() {
    this.createMode = null;
    this.createDialogRef = this.dialogService.open(this.createDialog, {
      closeOnBackdropClick: true,
    });
  }

  closeCreateDialog() {
    this.createDialogRef?.close();
    this.createDialogRef = undefined;
  }

  /** Design-system confirm dialog (never the browser's confirm()). Runs
   *  `action` only if the user confirms, then closes the dialog. */
  private confirmThen(message: string, action: () => void) {
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      closeOnBackdropClick: true,
      context: {
        message,
        confirmStatus: 'primary',
        onConfirm: () => {
          action();
          ref.close();
        },
      },
    });
  }

  /** Split each Moodle category path into segments (once), for level analysis. */
  /** Flattened Moodle category tree (each node = a cumulative path prefix). */
  get catRows(): { prefix: string; name: string; depth: number; ancestors: string[]; courseCount: number; hasChildren: boolean }[] {
    // Build nodes keyed by prefix; count leaf courses per exact category path.
    const nodes = new Map<string, { prefix: string; name: string; depth: number; ancestors: string[]; courseCount: number; children: Set<string> }>();
    const ensure = (prefix: string, name: string, depth: number, ancestors: string[]) => {
      let n = nodes.get(prefix);
      if (!n) { n = { prefix, name, depth, ancestors, courseCount: 0, children: new Set() }; nodes.set(prefix, n); }
      return n;
    };
    for (const cat of this.moodleCourses) {
      if (cat.key === 'uncategorized') continue;
      const segs = (cat.path || cat.label || '').split(' / ').map((s) => s.trim()).filter(Boolean);
      let acc = '';
      const anc: string[] = [];
      let leaf: any = null;
      segs.forEach((seg, i) => {
        const pfx = acc ? `${acc} / ${seg}` : seg;
        const node = ensure(pfx, seg, i, [...anc]);
        if (acc) nodes.get(acc)!.children.add(pfx);
        anc.push(pfx);
        acc = pfx;
        leaf = node;
      });
      if (leaf) leaf.courseCount += (cat.courses || []).filter((c) => c.course_id).length;
    }
    // DFS to a stable ordered, flattened list.
    const out: any[] = [];
    const roots = Array.from(nodes.values()).filter((n) => n.ancestors.length === 0).sort((a, b) => a.name.localeCompare(b.name));
    const walk = (n: any) => {
      out.push({ prefix: n.prefix, name: n.name, depth: n.depth, ancestors: n.ancestors, courseCount: n.courseCount, hasChildren: n.children.size > 0 });
      Array.from(n.children as Set<string>).map((p) => nodes.get(p)!).sort((a, b) => a.name.localeCompare(b.name)).forEach(walk);
    };
    roots.forEach(walk);
    return out;
  }

  get visibleCatRows() {
    return this.catRows.filter((r) => r.ancestors.every((a) => this.catExpanded.has(a)));
  }

  toggleCatExpand(prefix: string) {
    if (this.catExpanded.has(prefix)) this.catExpanded.delete(prefix);
    else this.catExpanded.add(prefix);
  }
  isCatExpanded(prefix: string): boolean {
    return this.catExpanded.has(prefix);
  }
  toggleProgramPrefix(prefix: string) {
    if (this.selectedProgramPrefixes.has(prefix)) this.selectedProgramPrefixes.delete(prefix);
    else this.selectedProgramPrefixes.add(prefix);
  }
  isProgramSelected(prefix: string): boolean {
    return this.selectedProgramPrefixes.has(prefix);
  }

  submitBulk() {
    const prefixes = Array.from(this.selectedProgramPrefixes);
    if (prefixes.length === 0) return;
    this.curriculumService.bulkFromCategories(prefixes, this.bulkCreateRules).subscribe({
      next: (r) => {
        this.toastr.success(
          `${r.programs_created} Studiengänge, ${r.items_created} Kurse, ${r.rules_created} Regeln`,
          'Bulk-Import fertig'
        );
        this.selectedProgramPrefixes.clear();
        this.createMode = null;
        this.closeCreateDialog();
        this.curriculumService.listPrograms().subscribe();
        if (r.programs?.[0]) this.selectProgram(r.programs[0].program_id);
      },
      error: () => this.toastr.danger('Bulk-Import fehlgeschlagen', 'Curriculum'),
    });
  }

  toggleCategory(key: string) {
    const i = this.selectedCategoryKeys.indexOf(key);
    if (i >= 0) this.selectedCategoryKeys.splice(i, 1);
    else this.selectedCategoryKeys.push(key);
  }

  isCategorySelected(key: string): boolean {
    return this.selectedCategoryKeys.includes(key);
  }

  submitFromCategories() {
    if (!this.categoryProgramName.trim() || this.selectedCategoryKeys.length === 0) return;
    this.curriculumService
      .createFromCategories(
        this.categoryProgramName.trim(),
        this.categoryProgramCode.trim() || null,
        this.selectedCategoryKeys,
        this.categoryModuleDepth
      )
      .subscribe({
        next: (r) => {
          this.toastr.success(
            `${r.groups_created} Kategorien, ${r.courses_created} Kurse übernommen`,
            'Curriculum angelegt'
          );
          if (r.warnings?.length) this.toastr.warning(r.warnings[0], 'Hinweis');
          this.categoryProgramName = '';
          this.categoryProgramCode = '';
          this.selectedCategoryKeys = [];
          this.createMode = null;
          this.closeCreateDialog();
          this.curriculumService.listPrograms().subscribe();
          this.selectProgram(r.program_id);
        },
        error: () => this.toastr.danger('Anlegen fehlgeschlagen', 'Curriculum'),
      });
  }

  ngOnDestroy(): void {
    this.clearImportPoll();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Row menu on a study program ---

  programMenuTag(program: StudyProgram): string {
    return `${this.programMenuPrefix}${program.id}`;
  }

  /** Same shape as the placements card: cached per row, because the context
   *  menu directive re-initialises when the array identity changes, and it
   *  throws outright on an empty list. */
  programMenuItems(program: StudyProgram): NbMenuItem[] {
    const cached = this.programMenuCache.get(program.id);
    if (cached) return cached;
    const items: NbMenuItem[] = [
      { title: 'Umbenennen', icon: 'edit-2-outline', data: { action: 'rename' } },
      { title: 'Löschen', icon: 'trash-2-outline', data: { action: 'delete' } },
    ];
    this.programMenuCache.set(program.id, items);
    return items;
  }

  private subscribeProgramMenu(): void {
    this.nbMenuService
      .onItemClick()
      .pipe(
        filter(({ tag }) => !!tag && tag.startsWith(this.programMenuPrefix)),
        takeUntil(this.destroy$)
      )
      .subscribe(({ tag, item }: any) => {
        const id = String(tag).slice(this.programMenuPrefix.length);
        const program = (this.curriculumService.programs$.value || []).find(
          (p) => p.id === id
        );
        if (!program) return;
        if (item?.data?.action === 'rename') this.startRenameProgram(program);
        if (item?.data?.action === 'delete') this.deleteProgram(program);
      });
  }

  // --- Agent release -------------------------------------------------------

  /** Agents that would actually read this program. Loaded with the tree, so
   *  the switch and its consequence are visible together — a release nobody
   *  consumes is the most common quiet failure here. */
  agentUsage: { id: string; name: string }[] = [];

  /** Dry run of the enrollment derivation for the selected program. Loaded
   *  with the assignment panel so the number is on screen BEFORE the switch is
   *  flipped — a program listing a first-semester service course everybody
   *  takes derives the whole tenant. */
  derivable: DerivablePreview | null = null;
  /** Guards the reconcile button against a double click. */
  derivePending = false;

  /** The program's master switch. Everything that inherits ends here. */
  get programRelease(): boolean {
    return !!this.tree?.agent_context_enabled;
  }

  /** What this row's own switch says: true, false, or null for "inherit". */
  ownRelease(row: CurriculumTreeRow): boolean | null {
    const value =
      row.kind === 'group'
        ? row.group?.agent_context_enabled
        : row.item?.agent_context_enabled;
    return value === undefined ? null : value;
  }

  /** What actually applies: the row's own switch if it has one, otherwise the
   *  nearest ancestor that has one, otherwise the program. */
  effectiveRelease(row: CurriculumTreeRow): boolean {
    const own = this.ownRelease(row);
    if (own !== null) return own;
    // ancestors are ordered outermost → innermost; walk back for the nearest.
    for (let i = row.ancestors.length - 1; i >= 0; i--) {
      const parent = this.treeRows.find(
        (r) => r.kind === 'group' && r.id === row.ancestors[i]
      );
      const parentOwn = parent ? this.ownRelease(parent) : null;
      if (parentOwn !== null) return parentOwn;
    }
    return this.programRelease;
  }

  releaseLabel(row: CurriculumTreeRow): string {
    const own = this.ownRelease(row);
    const effective = this.effectiveRelease(row);
    if (own === true) return 'Freigegeben (hier gesetzt)';
    if (own === false) return 'Gesperrt (hier gesetzt)';
    return effective
      ? 'Freigegeben (geerbt)'
      : 'Nicht freigegeben (geerbt)';
  }

  private loadAgentUsage() {
    if (!this.selectedProgramId) {
      this.agentUsage = [];
      return;
    }
    this.curriculumService.programAgentUsage(this.selectedProgramId).subscribe({
      next: (r) => (this.agentUsage = r.bots || []),
      error: () => (this.agentUsage = []),
    });
  }

  /** One sentence under the title: what the switch currently means, in the
   *  terms an education provider uses — not "enabled/disabled". */
  get releaseStatusText(): string {
    if (!this.programRelease) {
      return 'Gesperrt — kein Agent sieht die Struktur dieses Studiengangs.';
    }
    if (!this.agentUsage.length) {
      return 'Freigegeben, aber noch liest sie niemand: kein Agent ist in den Kursen platziert oder hat die Studiengang-Struktur eingeschaltet.';
    }
    return 'Agenten dürfen Modul, Nachbarkurse und Lernziele lesen. Kursinhalte bleiben ausgeschlossen.';
  }

  get agentUsageTooltip(): string {
    if (!this.agentUsage.length) {
      return 'Kein Agent liest diesen Studiengang: entweder ist keiner in seinen Kursen platziert, oder bei keinem ist die Studiengang-Struktur eingeschaltet.';
    }
    return 'Liest diesen Studiengang: ' + this.agentUsage.map((b) => b.name).join(', ');
  }

  toggleProgramRelease(enabled: boolean) {
    const program = (this.curriculumService.programs$.value || []).find(
      (p) => p.id === this.selectedProgramId
    );
    if (!program) return;
    this.curriculumService
      .updateProgram(program.id, {
        name: program.name,
        code: program.code ?? null,
        agent_context_enabled: enabled,
      })
      .subscribe({
        next: () => {
          this.curriculumService.listPrograms().subscribe();
          this.reloadTree();
          this.loadAgentUsage();
          // Say what was stored. A checkbox that writes silently leaves the
          // operator guessing whether a release actually took.
          this.toastr.success(
            enabled
              ? 'Studiengang für Agenten freigegeben.'
              : 'Freigabe zurückgenommen — Agenten lesen diesen Studiengang nicht mehr.',
            'Gespeichert'
          );
        },
        error: () => this.toastr.danger('Freigabe fehlgeschlagen', 'Studiengang'),
      });
  }

  private releaseSavedMessage(row: CurriculumTreeRow, value: boolean | null): string {
    const what = row.kind === 'group' ? 'Gruppe' : 'Kurs';
    if (value === true) return `${what} „${row.name}" für Agenten freigegeben.`;
    if (value === false) return `${what} „${row.name}" für Agenten gesperrt.`;
    return `${what} „${row.name}" erbt die Freigabe wieder von oben.`;
  }

  setRowRelease(row: CurriculumTreeRow, value: boolean | null) {
    if (row.kind === 'group' && row.group) {
      const g = row.group;
      this.curriculumService
        .updateGroup(g.id, {
          name: g.name,
          group_type: g.group_type ?? null,
          is_ordered: !!g.is_ordered,
          agent_context_enabled: value,
        })
        .subscribe({
          next: () => {
            this.reloadTree();
            this.loadAgentUsage();
            this.toastr.success(this.releaseSavedMessage(row, value), 'Gespeichert');
          },
          error: () => this.toastr.danger('Freigabe fehlgeschlagen', 'Gruppe'),
        });
      return;
    }
    const item = row.item;
    if (!item) return;
    this.curriculumService
      .updateItem(item.id, {
        course_id: item.course_id,
        group_id: item.group_id ?? null,
        semester: item.semester,
        is_mandatory: item.is_mandatory,
        learning_goals: item.learning_goals || [],
        agent_context_enabled: value,
      })
      .subscribe({
        next: () => {
          this.reloadTree();
          this.loadAgentUsage();
          this.toastr.success(this.releaseSavedMessage(row, value), 'Gespeichert');
        },
        error: () => this.toastr.danger('Freigabe fehlgeschlagen', 'Kurs'),
      });
  }

  // --- Verknüpfte Kursinhalte (deckt Ausschreibung 3.2 ab) ---
  //
  // The third access level: the curriculum release lets an agent NAME a
  // neighbouring course, a confirmed link lets it READ one. Lives behind the
  // row menu rather than as a column, because it is a per-course relationship
  // with its own approval state, not a flag.

  prereqCourseId: string | null = null;
  prereqCourseName = '';
  prereqMayRequest = false;

  openPrereq(row: CurriculumTreeRow): void {
    const courseId = row.item?.course_id;
    if (!courseId) return;
    this.prereqCourseId = courseId;
    this.prereqCourseName = row.name;
    // Requesting is an edit of this course, so the backend applies the same
    // rule as the placements: manager, admin, or responsible for it. Asking
    // here keeps the button from appearing only to fail with a 403.
    this.prereqMayRequest = false;
    this.curriculumService.mayEditCourse(courseId).subscribe({
      next: (may) => (this.prereqMayRequest = may),
      error: () => (this.prereqMayRequest = false),
    });
  }

  closePrereq(): void {
    this.prereqCourseId = null;
  }

  /** Course id → name, for the link card's dropdown and its labels.
   *
   *  Reuses `courseOptions`, which already flattens categories → courses and
   *  memoises the result. Reading `moodleCourses` directly here was wrong:
   *  that array holds CATEGORIES, whose entries have no `course_id`, so the
   *  filter dropped every one of them and the dropdown was always empty —
   *  the link could never be requested from this card.
   */
  get prereqCourseOptions(): { id: string; name: string; hasResponsible?: boolean }[] {
    // `hasResponsible` bleibt hier bewusst offen: diese Seite kennt die
    // Verantwortlichen nicht, und `undefined` heisst in der Auswahl
    // "waehlbar". Lieber die Anfrage laufen lassen und die klare Absage des
    // Backends zeigen, als hier zu raten. Die Moodle-Uebersicht, die es weiss,
    // reicht den Wert mit.
    return this.courseOptions.map((c) => ({ id: c.course_id, name: c.label }));
  }

  // --- Row menu on a tree row (group or course) ---

  rowMenuTag(row: CurriculumTreeRow): string {
    return `${this.rowMenuPrefix}${row.id}`;
  }

  rowMenuItems(row: CurriculumTreeRow): NbMenuItem[] {
    const canUp = this.canMove(row, -1);
    const canDown = this.canMove(row, 1);
    const fromSource = row.kind === 'group' && row.group?.source_type !== 'manual';
    const sig = `${row.kind}|${canUp}|${canDown}|${fromSource}|${this.ownRelease(row)}`;
    const cached = this.rowMenuCache.get(row.id);
    if (cached && cached.sig === sig) return cached.items;

    const own = this.ownRelease(row);
    const items: NbMenuItem[] = [];
    if (own !== true) {
      items.push({
        title: 'Für Agenten freigeben',
        icon: 'checkmark-circle-2-outline',
        data: { action: 'release:on' },
      });
    }
    if (own !== false) {
      items.push({
        title: 'Für Agenten sperren',
        icon: 'slash-outline',
        data: { action: 'release:off' },
      });
    }
    if (own !== null) {
      items.push({
        title: 'Freigabe vom übergeordneten erben',
        icon: 'undo-outline',
        data: { action: 'release:inherit' },
      });
    }
    // Omitted rather than greyed out: NbMenuItem has no disabled state, and a
    // greyed-out entry the menu still reports as clicked is worse than none.
    if (canUp) {
      items.push({ title: 'Nach oben', icon: 'arrow-ios-upward-outline', data: { action: 'up' } });
    }
    if (canDown) {
      items.push({ title: 'Nach unten', icon: 'arrow-ios-downward-outline', data: { action: 'down' } });
    }
    if (row.kind === 'group') {
      items.push(
        { title: 'Gruppe bearbeiten', icon: 'edit-2-outline', data: { action: 'edit' } },
        { title: 'Untergruppe anlegen', icon: 'folder-add-outline', data: { action: 'subgroup' } },
        { title: 'Kurs hinzufügen', icon: 'plus-outline', data: { action: 'addCourse' } }
      );
      if (fromSource) {
        items.push({
          title: 'Kurse aus Quelle übernehmen',
          icon: 'sync-outline',
          data: { action: 'expandSource' },
        });
      }
      items.push({ title: 'Gruppe löschen', icon: 'trash-2-outline', data: { action: 'delete' } });
    } else {
      items.push(
        { title: 'Semester und Pflicht/Wahl', icon: 'edit-2-outline', data: { action: 'edit' } },
        { title: 'Beschreibung und Lernziele', icon: 'list-outline', data: { action: 'content' } },
        { title: 'Kursinhalte verknüpfen', icon: 'link-2-outline', data: { action: 'prereq' } },
        { title: 'Kurs entfernen', icon: 'trash-2-outline', data: { action: 'delete' } }
      );
    }

    this.rowMenuCache.set(row.id, { sig, items });
    return items;
  }

  private subscribeRowMenu(): void {
    this.nbMenuService
      .onItemClick()
      .pipe(
        filter(({ tag }) => !!tag && tag.startsWith(this.rowMenuPrefix)),
        takeUntil(this.destroy$)
      )
      .subscribe(({ tag, item }: any) => {
        const rowId = String(tag).slice(this.rowMenuPrefix.length);
        const row = this.treeRows.find((r) => r.id === rowId);
        const action = item?.data?.action;
        if (!row || !action) return;
        this.runRowAction(row, action);
      });
  }

  private runRowAction(row: CurriculumTreeRow, action: string) {
    switch (action) {
      case 'up':
        this.moveRow(row, -1);
        break;
      case 'down':
        this.moveRow(row, 1);
        break;
      case 'edit':
        row.kind === 'group' ? this.startEditGroup(row) : this.startEditItem(row);
        break;
      case 'content':
        this.toggleGoals(row.item);
        break;
      case 'prereq':
        this.openPrereq(row);
        break;
      case 'subgroup':
        this.addChildGroup(row.id);
        break;
      case 'addCourse':
        this.startAddCourse(row.id);
        break;
      case 'expandSource':
        this.expandGroupSource(row);
        break;
      case 'release:on':
        this.setRowRelease(row, true);
        break;
      case 'release:off':
        this.setRowRelease(row, false);
        break;
      case 'release:inherit':
        this.setRowRelease(row, null);
        break;
      case 'delete':
        row.kind === 'group' ? this.deleteGroupRow(row.id) : this.deleteItemRow(row.item);
        break;
    }
  }

  startRenameProgram(program: StudyProgram) {
    this.renamingProgramId = program.id;
    this.renameProgramName = program.name;
  }

  cancelRenameProgram() {
    this.renamingProgramId = null;
    this.renameProgramName = '';
  }

  saveRenameProgram(program: StudyProgram) {
    const name = this.renameProgramName.trim();
    if (!name) return;
    this.curriculumService
      .updateProgram(program.id, { name, code: program.code ?? null })
      .subscribe({
        next: () => {
          this.cancelRenameProgram();
          this.curriculumService.listPrograms().subscribe();
        },
        error: () => this.toastr.danger('Umbenennen fehlgeschlagen', 'Studiengang'),
      });
  }

  selectProgram(id: string) {
    this.selectedProgramId = id;
    this.loadTree(id);
    this.loadAgentUsage();
    // Keep the rules panel open and reload it for the program just picked.
    // It used to close here, which read as "the Zuweisungsregeln button does
    // nothing": open the panel, pick the program you want the rule for, and
    // the panel you had just opened disappeared again.
    if (this.showRules) {
      this.loadRules();
      this.loadDerivable();
    }
  }

  loadTree(id: string) {
    this.curriculumService.getTree(id).subscribe({
      next: (t) => {
        this.tree = t;
        this.buildTreeRows();
        this.expandAll();
      },
      error: () => this.toastr.danger('Fehler beim Laden der Struktur', 'Curriculum'),
    });
    this.loadAttributes(id);
  }

  loadAttributes(id: string) {
    this.curriculumService.listAttributes(id).subscribe({
      next: (a) => {
        this.attributes = a;
        this.loadColPrefs(id);
      },
      error: () => (this.attributes = []),
    });
  }

  // --- Column visibility (persisted per program in localStorage) ---

  private colPrefsKey(programId: string): string {
    return `curriculum-cols-${programId}`;
  }
  private loadColPrefs(programId: string) {
    try {
      const raw = localStorage.getItem(this.colPrefsKey(programId));
      this.colHidden = new Set(raw ? JSON.parse(raw) : []);
    } catch {
      this.colHidden = new Set();
    }
  }
  private persistColPrefs() {
    if (!this.selectedProgramId) return;
    localStorage.setItem(this.colPrefsKey(this.selectedProgramId), JSON.stringify([...this.colHidden]));
  }
  isColVisible(key: string): boolean {
    return !this.colHidden.has(key);
  }
  toggleColumn(key: string) {
    if (this.colHidden.has(key)) this.colHidden.delete(key);
    else this.colHidden.add(key);
    this.persistColPrefs();
  }

  get visibleAttributes(): CurriculumAttribute[] {
    return this.attributes.filter((a) => !this.colHidden.has(`attr:${a.id}`));
  }

  // --- Bulk selection ---

  isItemSelected(itemId?: string): boolean {
    return !!itemId && this.selectedItemIds.has(itemId);
  }

  toggleItemSelection(itemId?: string) {
    if (!itemId) return;
    if (this.selectedItemIds.has(itemId)) this.selectedItemIds.delete(itemId);
    else this.selectedItemIds.add(itemId);
  }

  get selectedCount(): number {
    return this.selectedItemIds.size;
  }

  /** Select-all applies to the rows currently on screen, not to the whole
   *  program: selecting something the user cannot see is how people move
   *  courses they never meant to touch. */
  get visibleCourseIds(): string[] {
    return this.pagedRows
      .filter((row) => row.kind === 'course' && row.item)
      .map((row) => row.item!.id);
  }

  get allVisibleSelected(): boolean {
    const visible = this.visibleCourseIds;
    return visible.length > 0 && visible.every((id) => this.selectedItemIds.has(id));
  }

  toggleSelectAllVisible() {
    const visible = this.visibleCourseIds;
    if (this.allVisibleSelected) {
      visible.forEach((id) => this.selectedItemIds.delete(id));
    } else {
      visible.forEach((id) => this.selectedItemIds.add(id));
    }
  }

  clearSelection() {
    this.selectedItemIds.clear();
    this.bulkTargetGroupId = null;
  }

  /** Flat list of groups for the move target, indented by depth so a nested
   *  tree stays readable in a single select. */
  get groupOptions(): { id: string; label: string }[] {
    const groups = this.treeRows.filter((row) => row.kind === 'group');
    const sig = groups.map((row) => `${row.id}:${row.depth}:${row.name}`).join('|');
    if (this.groupOptionsMemo?.sig === sig) return this.groupOptionsMemo.value;
    const value = groups.map((row) => ({
      id: row.id,
      label: `${'\u00a0\u00a0'.repeat(row.depth)}${row.name}`,
    }));
    this.groupOptionsMemo = { sig, value };
    return value;
  }

  moveSelectedToGroup() {
    if (!this.selectedCount) return;
    const ids = Array.from(this.selectedItemIds);
    this.curriculumService
      .bulkMoveItems(ids, this.bulkTargetGroupId)
      .subscribe({
        next: (res) => {
          this.toastr.success(
            `${res.moved} Kurs${res.moved === 1 ? '' : 'e'} verschoben`,
            'Gruppe'
          );
          this.clearSelection();
          this.reloadTree();
        },
        error: () => this.toastr.danger('Verschieben fehlgeschlagen', 'Gruppe'),
      });
  }

  get fullColspan(): number {
    return (
      4 + // select + name + release + actions (always shown)
      (this.isColVisible('type') ? 1 : 0) +
      (this.isColVisible('sem') ? 1 : 0) +
      (this.isColVisible('goals') ? 1 : 0) +
      this.visibleAttributes.length
    );
  }

  // --- Pagination over the flattened visible rows ---

  get pagedRows(): CurriculumTreeRow[] {
    const total = this.visibleRows.length;
    const maxPage = Math.max(0, Math.ceil(total / this.treePageSize) - 1);
    const page = Math.min(this.treePage, maxPage);
    const start = page * this.treePageSize;
    return this.visibleRows.slice(start, start + this.treePageSize);
  }
  onTreePageChange(page: number) {
    this.treePage = Math.max(0, page);
  }
  onTreePageSizeChange(size: number) {
    this.treePageSize = size;
    this.treePage = 0;
  }

  // --- Attribute drag & drop reorder (persists display_order) ---

  dropAttribute(event: CdkDragDrop<CurriculumAttribute[]>) {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.attributes, event.previousIndex, event.currentIndex);
    // Re-sequence display_order to match the new visual order, persist all.
    this.attributes.forEach((a, i) => {
      if (a.display_order !== i) {
        a.display_order = i;
        this.curriculumService
          .updateAttribute(a.id, { label: a.label, attr_type: a.attr_type, display_order: i })
          .subscribe();
      }
    });
  }

  attrValue(item: CurriculumItem | undefined, attr: CurriculumAttribute): any {
    const v = item?.custom_attributes?.[attr.id];
    if (attr.attr_type === 'boolean') return v ? 'ja' : v === false ? 'nein' : '';
    return v ?? '';
  }

  // --- Attribute manager ---

  toggleAttrManager() {
    this.showAttrManager = !this.showAttrManager;
  }

  addAttribute() {
    if (!this.selectedProgramId || !this.newAttrLabel.trim()) return;
    this.curriculumService
      .createAttribute(this.selectedProgramId, {
        label: this.newAttrLabel.trim(),
        attr_type: this.newAttrType,
        options:
          this.newAttrType === 'dropdown'
            ? this.newAttrOptions.split(',').map((o) => o.trim()).filter(Boolean)
            : [],
        display_order: this.attributes.length,
        is_required: this.newAttrRequired,
      })
      .subscribe({
        next: () => {
          this.newAttrLabel = '';
          this.newAttrOptions = '';
          this.newAttrType = 'text';
          this.newAttrRequired = false;
          this.loadAttributes(this.selectedProgramId!);
        },
        error: () => this.toastr.danger('Spalte konnte nicht angelegt werden', 'Attribut'),
      });
  }

  deleteAttribute(attr: CurriculumAttribute) {
    this.confirmThen(`Spalte „${attr.label}" löschen?`, () => {
      this.curriculumService.deleteAttribute(attr.id).subscribe({
        next: () => this.loadAttributes(this.selectedProgramId!),
        error: () => this.toastr.danger('Löschen fehlgeschlagen', 'Attribut'),
      });
    });
  }

  moveAttribute(attr: CurriculumAttribute, dir: -1 | 1) {
    const i = this.attributes.indexOf(attr);
    const j = i + dir;
    if (j < 0 || j >= this.attributes.length) return;
    const a = this.attributes[i];
    const b = this.attributes[j];
    this.curriculumService.updateAttribute(a.id, { label: a.label, attr_type: a.attr_type, display_order: b.display_order }).subscribe();
    this.curriculumService
      .updateAttribute(b.id, { label: b.label, attr_type: b.attr_type, display_order: a.display_order })
      .subscribe({ next: () => this.loadAttributes(this.selectedProgramId!) });
  }

  private reloadTree() {
    if (this.selectedProgramId) this.loadTree(this.selectedProgramId);
  }

  // Stopgap to unblock the dev-server compile: refreshTab() was called after
  // group mutations but never defined. Delegates to the existing tree reload,
  // matching the reloadTree() calls used elsewhere for the same purpose.
  private refreshTab() {
    this.reloadTree();
  }

  toggleRules() {
    this.showRules = !this.showRules;
    if (this.showRules) {
      this.loadRules();
      this.loadDerivable();
    }
  }

  private loadRules() {
    if (!this.selectedProgramId) {
      this.rules = [];
      return;
    }
    this.curriculumService.listRules(this.selectedProgramId).subscribe({
      next: (r) => (this.rules = r),
      error: () => this.toastr.danger('Fehler beim Laden der Regeln', 'Curriculum'),
    });
  }

  // --- Tree-table model ---

  private buildTreeRows() {
    const rows: CurriculumTreeRow[] = [];

    /**
     * @param guides  through-bands for this level's rows (length = depth - 1)
     */
    const walk = (
      nodes: any[],
      depth: number,
      ancestors: string[],
      guides: boolean[]
    ) => {
      const list = nodes || [];
      list.forEach((g, gi) => {
        const groupIsLast = gi === list.length - 1;
        rows.push({
          kind: 'group', id: g.id, depth, ancestors: [...ancestors],
          name: g.name, group: g, guides: [...guides], isLast: groupIsLast,
        });

        // A depth-0 group has no band to its left, so whether it is the last
        // sibling changes nothing for its children. Deeper down it decides
        // whether the parent's branch keeps running past them.
        const childGuides =
          depth === 0 ? [...guides] : [...guides, !groupIsLast];
        const childAnc = [...ancestors, g.id];

        // Render order inside a group: its own courses first, then subgroups.
        // Last-ness has to be counted across both, otherwise the final course
        // of a group that also has subgroups would end the line too early.
        const items = g.items || [];
        const kids = g.children || [];
        const childCount = items.length + kids.length;

        items.forEach((it: CurriculumItem, k: number) => {
          rows.push({
            kind: 'course', id: it.id, depth: depth + 1, ancestors: childAnc,
            name: this.courseName(it.course_id), item: it,
            guides: childGuides, isLast: k === childCount - 1,
          });
        });
        if (kids.length) walk(kids, depth + 1, childAnc, childGuides);
      });
    };

    if (this.tree) {
      walk(this.tree.groups, 0, [], []);
      const loose = this.tree.ungrouped || [];
      loose.forEach((it: CurriculumItem, k: number) => {
        rows.push({
          kind: 'course', id: it.id, depth: 0, ancestors: [],
          name: this.courseName(it.course_id), item: it,
          guides: [], isLast: k === loose.length - 1,
        });
      });
    }
    this.treeRows = rows;
  }

  get visibleRows(): CurriculumTreeRow[] {
    return this.treeRows.filter((r) => r.ancestors.every((a) => this.expanded.has(a)));
  }

  // --- What a row is, what it is for, where it came from -------------------
  //
  // These three were tangled: the icon encoded PROVENANCE (folder = adopted
  // from a Moodle category, grid = hand-made), the "Typ" column encoded the
  // same bit again for groups ("Kategorie" vs "Gruppe"), and for courses that
  // same column switched to a completely unrelated question (Pflicht/Wahl).
  // Two rows called "Category 1" — one adopted, one hand-made — therefore
  // differed only by an icon nobody could decode.
  //
  // Now each channel answers exactly one question:
  //   icon   → WHAT the row is        (Kurs · Modul · Gruppe)
  //   Typ    → what the row is FOR    (Pflicht/Wahl · Modul/Gruppe)
  //   Tag    → WHERE it came from     (aus Moodle, sonst nichts)

  /** A group that stands for a module — several courses forming one unit.
   *  Set by the category import at the chosen module depth. */
  isModuleGroup(row: CurriculumTreeRow): boolean {
    return (row.group?.group_type || '').trim().toLowerCase() === 'module';
  }

  rowIcon(row: CurriculumTreeRow): string {
    if (row.kind === 'course') return 'book-open-outline';
    return this.isModuleGroup(row) ? 'grid-outline' : 'folder-outline';
  }

  rowIconTooltip(row: CurriculumTreeRow): string {
    if (row.kind === 'course') return 'Kurs — die kleinste Einheit, entspricht einem Kurs im LMS.';
    return this.isModuleGroup(row)
      ? 'Modul — fasst mehrere Kurse zu einer Einheit zusammen.'
      : 'Gruppe — gliedert den Studiengang, ohne selbst ein Modul zu sein.';
  }

  rowTypeLabel(row: CurriculumTreeRow): string {
    if (row.kind === 'course') return row.item?.is_mandatory ? 'Pflicht' : 'Wahl';
    if (this.isModuleGroup(row)) return 'Modul';
    // A hand-typed group_type is shown as entered; empty means a plain group.
    return (row.group?.group_type || '').trim() || 'Gruppe';
  }

  rowTypeTooltip(row: CurriculumTreeRow): string {
    if (row.kind === 'course') {
      return row.item?.is_mandatory
        ? 'Pflichtkurs — gehört verbindlich zum Studiengang.'
        : 'Wahlkurs — kann belegt werden, muss aber nicht.';
    }
    return this.isModuleGroup(row)
      ? 'Modul — mehrere Kurse als eine Einheit.'
      : 'Gruppe — reine Gliederungsebene.';
  }

  /** Provenance, deliberately NOT in the icon: it decides what a resync may
   *  overwrite, not what the node is. Only shown when it is not the default. */
  rowOrigin(row: CurriculumTreeRow): string | null {
    if (row.kind !== 'group') return null;
    return row.group?.source_type === 'moodle_category' ? 'Moodle' : null;
  }

  rowOriginTooltip(row: CurriculumTreeRow): string {
    return 'Aus einer Moodle-Kategorie übernommen. Name und Kurse können von einem erneuten Import aktualisiert werden; von Hand angelegte Gruppen nicht.';
  }

  rowHasChildren(row: CurriculumTreeRow): boolean {
    return row.kind === 'group' && !!row.group &&
      ((row.group.items?.length || 0) > 0 || (row.group.children?.length || 0) > 0);
  }

  toggleExpand(id: string) {
    if (this.expanded.has(id)) this.expanded.delete(id);
    else this.expanded.add(id);
  }
  isExpanded(id: string): boolean {
    return this.expanded.has(id);
  }
  expandAll() {
    this.expanded = new Set(this.treeRows.filter((r) => r.kind === 'group').map((r) => r.id));
  }
  collapseAll() {
    this.expanded = new Set();
  }

  // --- Inline group creation ---

  addRootGroup() {
    this.createGroupThenEdit(null);
  }
  addChildGroup(parentId: string) {
    this.createGroupThenEdit(parentId);
  }
  /** No browser prompt: create a placeholder group, then drop its row straight
   *  into inline-edit so the user names it in-table (design-system flow). */
  private createGroupThenEdit(parentId: string | null) {
    if (!this.selectedProgramId) return;
    this.curriculumService
      .createGroup(this.selectedProgramId, { name: 'Neue Gruppe', parent_id: parentId })
      .subscribe({
        next: (g) => {
          if (parentId) this.expanded.add(parentId);
          this.editGroupId = g.id;
          this.editGroupName = g.name;
          this.editGroupType = g.group_type || '';
          this.reloadTree();
        },
        error: () => this.toastr.danger('Fehler beim Anlegen', 'Gruppe'),
      });
  }

  // --- Inline edit: group (name, type) ---

  startEditGroup(row: CurriculumTreeRow) {
    if (!row.group) return;
    this.editItemId = null;
    this.editGroupId = row.id;
    this.editGroupName = row.group.name;
    this.editGroupType = row.group.group_type || '';
    this.editGroupOrdered = !!row.group.is_ordered;
  }
  saveEditGroup() {
    if (!this.editGroupId || !this.editGroupName.trim()) return;
    this.curriculumService
      .updateGroup(this.editGroupId, {
        name: this.editGroupName.trim(),
        group_type: this.editGroupType.trim() || null,
        is_ordered: this.editGroupOrdered,
      })
      .subscribe({
        next: () => {
          this.cancelEditGroup();
          this.reloadTree();
        },
        error: () => this.toastr.danger('Speichern fehlgeschlagen', 'Gruppe'),
      });
  }
  cancelEditGroup() {
    this.editGroupId = null;
    this.editGroupName = '';
    this.editGroupType = '';
    this.editGroupOrdered = false;
  }

  // --- Inline edit: course (semester, mandatory) ---

  startEditItem(row: CurriculumTreeRow) {
    if (!row.item) return;
    this.editGroupId = null;
    this.editItemId = row.id;
    this.editItemSemester = row.item.semester;
    this.editItemMandatory = row.item.is_mandatory;
    this.editItemAttrs = { ...(row.item.custom_attributes || {}) };
  }
  saveEditItem(row: CurriculumTreeRow) {
    if (!this.editItemId || !row.item) return;
    this.curriculumService
      .updateItem(row.item.id, {
        course_id: row.item.course_id,
        group_id: row.item.group_id ?? null,
        semester: this.editItemSemester,
        is_mandatory: this.editItemMandatory,
        learning_goals: row.item.learning_goals || [],
        custom_attributes: this.editItemAttrs,
      })
      .subscribe({
        next: () => {
          this.cancelEditItem();
          this.reloadTree();
        },
        error: () => this.toastr.danger('Speichern fehlgeschlagen', 'Kurs'),
      });
  }
  cancelEditItem() {
    this.editItemId = null;
  }

  // --- Row reordering (siblings share kind + ancestor chain) ---

  private siblingsOf(row: CurriculumTreeRow): CurriculumTreeRow[] {
    const key = row.ancestors.join('>');
    return this.treeRows.filter(
      (r) => r.kind === row.kind && r.ancestors.join('>') === key
    );
  }

  canMove(row: CurriculumTreeRow, dir: -1 | 1): boolean {
    const sibs = this.siblingsOf(row);
    const i = sibs.indexOf(row);
    return i + dir >= 0 && i + dir < sibs.length;
  }

  moveRow(row: CurriculumTreeRow, dir: -1 | 1) {
    const sibs = this.siblingsOf(row);
    const i = sibs.indexOf(row);
    const j = i + dir;
    if (j < 0 || j >= sibs.length) return;
    const a = sibs[i];
    const b = sibs[j];
    const ao = this.rowOrder(a, i);
    const bo = this.rowOrder(b, j);
    // Swap display_order between the two adjacent siblings, persist both.
    this.persistOrder(a, bo);
    this.persistOrder(b, ao, () => {
      this.reloadTree();
      this.markMoved(row.id);
    });
  }

  /** Highlight the row after the tree came back, not before: the reload
   *  rebuilds the rows, so a class set earlier would be thrown away. */
  private markMoved(rowId: string) {
    clearTimeout(this.justMovedTimer);
    this.justMovedRowId = rowId;
    this.justMovedTimer = setTimeout(() => (this.justMovedRowId = null), 1400);
  }

  isJustMoved(rowId: string): boolean {
    return this.justMovedRowId === rowId;
  }

  /** One span per level, so the tree can draw real guide lines instead of a
   *  single blank indent. */
  guideLevels(depth: number): number[] {
    return Array.from({ length: Math.max(0, depth) }, (_, index) => index);
  }

  /** Why the order matters for this group — the tooltip has to say it,
   *  otherwise "Aufbaureihe" is a word without consequence. */
  orderHint(row: CurriculumTreeRow): string {
    return row.group?.is_ordered
      ? 'Aufbaureihe: die Reihenfolge der Kurse wird als aufeinander aufbauend gelesen — ' +
          'spätere Kurse setzen frühere voraus.'
      : 'Sammlung: die Reihenfolge ist nur Sortierung, die Kurse bauen nicht aufeinander auf.';
  }

  private rowOrder(row: CurriculumTreeRow, fallback: number): number {
    const v = row.kind === 'group' ? row.group?.display_order : row.item?.display_order;
    return v ?? fallback;
  }

  private persistOrder(row: CurriculumTreeRow, order: number, done?: () => void) {
    if (row.kind === 'group' && row.group) {
      this.curriculumService
        .updateGroup(row.id, {
          name: row.group.name,
          group_type: row.group.group_type ?? null,
          display_order: order,
        })
        .subscribe({ next: () => done?.(), error: () => done?.() });
    } else if (row.kind === 'course' && row.item) {
      this.curriculumService
        .updateItem(row.item.id, {
          course_id: row.item.course_id,
          group_id: row.item.group_id ?? null,
          semester: row.item.semester,
          is_mandatory: row.item.is_mandatory,
          learning_goals: row.item.learning_goals || [],
          display_order: order,
        })
        .subscribe({ next: () => done?.(), error: () => done?.() });
    } else {
      done?.();
    }
  }

  /** Rule ref-key dropdown options — real Moodle categories (no free-text). */
  get ruleRefOptions(): { key: string; label: string }[] {
    const cats = this.moodleCourses.filter((c) => c.key !== 'uncategorized');
    const sig = cats.map((c) => `${c.key}:${c.count}`).join('|');
    if (this.ruleRefOptionsMemo?.sig === sig) return this.ruleRefOptionsMemo.value;
    const value = cats.map((c) => ({
      key: c.key,
      label: `${c.label} (${c.count})`,
    }));
    this.ruleRefOptionsMemo = { sig, value };
    return value;
  }

  // --- Inline course add (from Moodle courses) ---

  startAddCourse(groupId: string) {
    this.addCourseForGroup = this.addCourseForGroup === groupId ? null : groupId;
    this.newCourseId = null;
  }
  confirmAddCourse(groupId: string) {
    if (!this.selectedProgramId || !this.newCourseId) return;
    this.curriculumService
      .createItem(this.selectedProgramId, {
        course_id: this.newCourseId,
        group_id: groupId,
        semester: 1,
        is_mandatory: true,
        learning_goals: [],
      })
      .subscribe({
        next: () => {
          this.addCourseForGroup = null;
          this.newCourseId = null;
          this.reloadTree();
        },
        error: () => this.toastr.danger('Kurs konnte nicht hinzugefügt werden', 'Curriculum'),
      });
  }

  // --- Per-course learning goals (inline editor) ---

  toggleGoals(item?: CurriculumItem) {
    if (!item) return;
    if (this.goalsOpen.has(item.id)) {
      this.goalsOpen.delete(item.id);
    } else {
      this.goalDrafts[item.id] = (item.learning_goals || []).map((g) => ({ ...g }));
      this.descriptionDrafts[item.id] = item.description || '';
      this.sourceDrafts[item.id] = {
        description: item.description_source || 'manual',
        goals: item.goals_source || 'manual',
      };
      this.goalsOpen.add(item.id);
    }
  }
  goalsVisible(itemId: string): boolean {
    return this.goalsOpen.has(itemId);
  }
  draftGoals(itemId: string): LearningGoal[] {
    return this.goalDrafts[itemId] || [];
  }
  addDraftGoal(itemId: string) {
    (this.goalDrafts[itemId] = this.goalDrafts[itemId] || []).push({
      id: '',
      description: '',
      bloom_level: 'understand',
      status: 'default',
    });
  }
  removeDraftGoal(itemId: string, i: number) {
    this.goalDrafts[itemId]?.splice(i, 1);
  }
  descriptionDraft(itemId: string): string {
    return this.descriptionDrafts[itemId] || '';
  }

  setDescriptionDraft(itemId: string, value: string) {
    this.descriptionDrafts[itemId] = value;
    // Typed by a person → no longer an adopted value, so a later resync keeps
    // its hands off it.
    const sources = this.sourceDrafts[itemId];
    if (sources) sources.description = 'manual';
  }

  sourceLabel(itemId: string, field: 'description' | 'goals'): string | null {
    const source = this.sourceDrafts[itemId]?.[field];
    if (!source || source === 'manual') return null;
    return source === 'moodle' ? 'aus Moodle' : 'aus Kursstruktur';
  }

  isProposalLoading(itemId: string): boolean {
    return !!this.proposalLoading[itemId];
  }

  /** Fetch what the LMS could contribute and put it into the open drafts.
   *  Nothing is saved here — the user still has to press Speichern, and an
   *  existing text is only replaced when it is empty or itself adopted. */
  adoptFromLms(item?: CurriculumItem) {
    if (!item) return;
    this.proposalLoading[item.id] = true;
    this.curriculumService.contentProposal(item.id).subscribe({
      next: (proposal) => {
        this.proposalLoading[item.id] = false;
        const sources =
          this.sourceDrafts[item.id] ||
          (this.sourceDrafts[item.id] = { description: 'manual', goals: 'manual' });

        const current = (this.descriptionDrafts[item.id] || '').trim();
        if (proposal.description) {
          if (!current || sources.description !== 'manual') {
            this.descriptionDrafts[item.id] = proposal.description;
            sources.description = proposal.description_source || 'moodle';
          } else {
            this.toastr.info(
              'Beschreibung bleibt — sie wurde von Hand geschrieben.',
              'Übernahme'
            );
          }
        }

        // Goals are appended, not replaced: a curriculum goal someone wrote is
        // a statement about the programme, not a copy of the course content.
        const existing = new Set(
          (this.goalDrafts[item.id] || []).map((g) =>
            (g.description || '').trim().toLowerCase()
          )
        );
        const added = (proposal.goals || []).filter(
          (g) => !existing.has((g.description || '').trim().toLowerCase())
        );
        if (added.length) {
          this.goalDrafts[item.id] = [
            ...(this.goalDrafts[item.id] || []),
            ...added.map((g) => ({ ...g })),
          ];
          sources.goals = proposal.goals_source || 'structure';
        }

        if (!proposal.description && !added.length) {
          this.toastr.info(
            'Im LMS liegt für diesen Kurs nichts Übernehmbares.',
            'Übernahme'
          );
        }
      },
      error: () => {
        this.proposalLoading[item.id] = false;
        this.toastr.danger('Übernahme fehlgeschlagen', 'Kurs');
      },
    });
  }

  saveGoals(item?: CurriculumItem) {
    if (!item || !this.selectedProgramId) return;
    const goals = (this.goalDrafts[item.id] || []).filter((g) => g.description.trim());
    const sources = this.sourceDrafts[item.id];
    this.curriculumService
      .updateItem(item.id, {
        course_id: item.course_id,
        group_id: item.group_id ?? null,
        semester: item.semester,
        is_mandatory: item.is_mandatory,
        learning_goals: goals,
        description: (this.descriptionDrafts[item.id] || '').trim() || null,
        description_source: sources?.description || 'manual',
        goals_source: sources?.goals || 'manual',
      })
      .subscribe({
        next: () => {
          this.toastr.success('Lernziele gespeichert', '');
          this.goalsOpen.delete(item.id);
          this.reloadTree();
        },
        error: () => this.toastr.danger('Speichern fehlgeschlagen', 'Lernziele'),
      });
  }

  deleteGroupRow(id: string) {
    this.confirmThen('Gruppe wirklich löschen? Untergruppen/Kurse werden ungruppiert.', () => {
      this.curriculumService.deleteGroup(id).subscribe({
        next: () => {
          this.toastr.success('Gruppe gelöscht', '');
          this.reloadTree();
        },
        error: () => this.toastr.danger('Fehler beim Löschen', 'Gruppe'),
      });
    });
  }
  deleteItemRow(item?: CurriculumItem) {
    if (!item) return;
    this.confirmThen('Kurs aus Curriculum entfernen?', () => {
      this.curriculumService.deleteItem(item.id).subscribe({
        next: () => {
          this.toastr.success('Entfernt', '');
          this.reloadTree();
        },
        error: () => this.toastr.danger('Fehler beim Entfernen', 'Curriculum'),
      });
    });
  }
  expandGroupSource(row: CurriculumTreeRow) {
    this.curriculumService.expandGroup(row.id).subscribe({
      next: (r) => {
        this.toastr.success(`${r.added} Kurs(e) übernommen`, 'Gruppe befüllt');
        this.reloadTree();
      },
      error: () => this.toastr.danger('Quelle nicht auflösbar', 'Gruppe'),
    });
  }

  // --- Struktur / groups ---

  submitGroup() {
    if (!this.selectedProgramId || this.groupForm.invalid) {
      this.groupForm.markAllAsTouched();
      return;
    }
    const v = this.groupForm.value;
    this.curriculumService
      .createGroup(this.selectedProgramId, {
        name: v.name,
        group_type: v.group_type || null,
        source_type: v.source_type,
        source_ref: v.source_ref || null,
      })
      .subscribe({
        next: () => {
          this.toastr.success('Gruppe angelegt', v.name);
          this.groupForm.reset({ name: '', group_type: '', source_type: 'manual', source_ref: '' });
          this.refreshTab();
        },
        error: () => this.toastr.danger('Fehler beim Anlegen', 'Gruppe'),
      });
  }

  deleteGroup(groupId: string) {
    this.confirmThen('Gruppe wirklich löschen? Kurse der Gruppe werden ungruppiert.', () => {
      this.curriculumService.deleteGroup(groupId).subscribe({
        next: () => {
          this.toastr.success('Gruppe gelöscht', '');
          this.refreshTab();
          if (this.selectedProgramId) this.curriculumService.listItems(this.selectedProgramId).subscribe();
        },
        error: () => this.toastr.danger('Fehler beim Löschen', 'Gruppe'),
      });
    });
  }

  expandGroup(groupId: string) {
    this.curriculumService.expandGroup(groupId).subscribe({
      next: (r) => {
        this.toastr.success(`${r.added} Kurs(e) übernommen, ${r.skipped} übersprungen`, 'Gruppe befüllt');
        this.refreshTab();
        if (this.selectedProgramId) this.curriculumService.listItems(this.selectedProgramId).subscribe();
      },
      error: () => this.toastr.danger('Quelle konnte nicht aufgelöst werden', 'Gruppe'),
    });
  }

  // --- Import ---

  onImportFile(event: Event) {
    const input = event.target as HTMLInputElement;
    this.acceptImportFile(input.files && input.files.length ? input.files[0] : null);
    // Reset so picking the same file twice still fires a change event.
    input.value = '';
  }

  onImportDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.importDragOver = true;
  }

  onImportDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.importDragOver = false;
  }

  onImportFileDropped(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.importDragOver = false;
    const dropped = event.dataTransfer?.files;
    this.acceptImportFile(dropped && dropped.length ? dropped[0] : null);
  }

  clearImportFile() {
    this.importFile = null;
    this.importFileError = null;
  }

  /** One document per import — the parser builds a single study program, so a
   *  second file would silently be ignored. Say it instead. */
  private acceptImportFile(file: File | null) {
    this.importFileError = null;
    if (!file) {
      this.importFile = null;
      return;
    }
    const name = file.name.toLowerCase();
    if (!['.pdf', '.docx', '.txt'].some((ext) => name.endsWith(ext))) {
      this.importFile = null;
      this.importFileError = this.translate.instant(
        'pages.curriculum.import.unsupported'
      );
      return;
    }
    this.importFile = file;
  }

  startImport() {
    if (!this.importFile) return;
    this.importStatus = 'queued';
    this.importParsed = null;
    this.importWarnings = [];
    this.importError = null;
    this.curriculumService.uploadImport(this.importFile).subscribe({
      next: (r) => {
        this.importJobId = r.job_id;
        this.pollImport();
      },
      error: () => {
        this.importStatus = 'error';
        this.importError = 'Upload fehlgeschlagen';
      },
    });
  }

  private pollImport() {
    if (!this.importJobId) return;
    this.clearImportPoll();
    this.importPoll = setInterval(() => {
      if (!this.importJobId) return;
      this.curriculumService.getImportStatus(this.importJobId).subscribe({
        next: (s) => {
          this.importStatus = s.status;
          if (s.status === 'done') {
            this.importParsed = s.parsed_curriculum;
            this.importWarnings = s.warnings || [];
            this.clearImportPoll();
          } else if (s.status === 'error') {
            this.importError = s.error_message || 'Analyse fehlgeschlagen';
            this.clearImportPoll();
          }
        },
        error: () => {
          this.importStatus = 'error';
          this.importError = 'Statusabfrage fehlgeschlagen';
          this.clearImportPoll();
        },
      });
    }, 3000);
  }

  private clearImportPoll() {
    if (this.importPoll) {
      clearInterval(this.importPoll);
      this.importPoll = null;
    }
  }

  commitImport() {
    if (!this.importParsed) return;
    this.curriculumService.commitImport(this.importParsed).subscribe({
      next: (r) => {
        this.toastr.success(
          `${r.groups_created} Gruppen, ${r.courses_created} Kurse angelegt`,
          'Curriculum importiert'
        );
        if (r.warnings?.length) {
          this.toastr.warning(`${r.warnings.length} Hinweis(e) — Kurse ggf. manuell zuordnen`, 'Import');
        }
        this.resetImport();
        this.closeCreateDialog();
        this.curriculumService.listPrograms().subscribe();
        this.selectProgram(r.program_id);
      },
      error: () => this.toastr.danger('Import fehlgeschlagen', 'Curriculum'),
    });
  }

  resetImport() {
    this.clearImportPoll();
    this.importFile = null;
    this.importJobId = null;
    this.importStatus = 'idle';
    this.importParsed = null;
    this.importWarnings = [];
    this.importError = null;
  }

  // --- Rules ---

  submitRule() {
    if (!this.selectedProgramId) {
      this.toastr.warning(
        'Wähle links einen Studiengang — Regeln gelten immer für einen Studiengang.',
        'Regeln'
      );
      return;
    }
    if (this.ruleForm.invalid) {
      this.ruleForm.markAllAsTouched();
      return;
    }
    const v = this.ruleForm.value;
    this.curriculumService
      .createRule(this.selectedProgramId, {
        rule_type: v.rule_type,
        ref_key: v.ref_key || null,
        default_start_semester: v.default_start_semester || null,
      })
      .subscribe({
        next: () => {
          this.toastr.success('Regel angelegt', '');
          this.ruleForm.reset({ rule_type: 'moodle_category', ref_key: '', default_start_semester: 1 });
          this.refreshTab();
        },
        error: () => this.toastr.danger('Fehler beim Anlegen (Duplikat?)', 'Regel'),
      });
  }

  deleteRule(ruleId: string) {
    this.confirmThen('Regel wirklich löschen?', () => {
      this.curriculumService.deleteRule(ruleId).subscribe({
        next: () => {
          this.toastr.success('Regel gelöscht', '');
          this.refreshTab();
        },
        error: () => this.toastr.danger('Fehler beim Löschen', 'Regel'),
      });
    });
  }

  applyRules() {
    // Used to return in silence. A button that does nothing and says nothing
    // is indistinguishable from a broken one — which is exactly how this was
    // reported.
    if (!this.selectedProgramId) {
      this.toastr.warning(
        'Wähle links einen Studiengang — Regeln gelten immer für einen Studiengang.',
        'Regeln'
      );
      return;
    }
    this.curriculumService.applyRules(this.selectedProgramId).subscribe({
      next: (r) =>
        this.toastr.success(
          `${r.target} Lerner gematcht — ${r.added} zugewiesen, ${r.removed} entfernt`,
          'Regeln angewendet'
        ),
      error: () => this.toastr.danger('Anwenden fehlgeschlagen', 'Regeln'),
    });
  }

  // --- Ableitung aus der Kursbelegung ---

  private loadDerivable() {
    if (!this.selectedProgramId) {
      this.derivable = null;
      return;
    }
    this.curriculumService.derivableLearners(this.selectedProgramId).subscribe({
      next: (d) => (this.derivable = d),
      error: () => (this.derivable = null),
    });
  }

  /** True while the program has the switch on. Read from the preview rather
   *  than the program list so it stays in step with what the backend stored. */
  get autoEnrollOn(): boolean {
    return !!this.derivable?.auto_enroll_from_courses;
  }

  get derivableHint(): string {
    const d = this.derivable;
    if (!d) return '';
    if (!d.courses_linked) {
      return 'Dieser Studiengang hat noch keine Kurse — ohne Kurse gibt es nichts abzuleiten.';
    }
    if (!d.courses_with_lms_link) {
      return `${d.courses_linked} Kurse im Studiengang, aber keiner davon zeigt auf einen LMS-Kurs. Ohne diese Verbindung gibt es keine Teilnehmerliste.`;
    }
    const blocked = d.learners_blocked_by_stronger_source
      ? ` ${d.learners_blocked_by_stronger_source} davon sind schon manuell oder per Regel zugewiesen und bleiben unberührt.`
      : '';
    return (
      `${d.learners_derivable} Lerner ergeben sich aus ${d.courses_with_lms_link} ` +
      `von ${d.courses_linked} Kursen.${blocked}`
    );
  }

  setAutoEnroll(enabled: boolean) {
    const program = (this.curriculumService.programs$.value || []).find(
      (p) => p.id === this.selectedProgramId
    );
    if (!program) {
      this.toastr.warning(
        'Wähle links einen Studiengang — die Ableitung gilt immer für einen Studiengang.',
        'Zuweisung'
      );
      return;
    }
    this.curriculumService
      .updateProgram(program.id, {
        name: program.name,
        code: program.code ?? null,
        auto_enroll_from_courses: enabled,
      })
      .subscribe({
        next: () => {
          this.toastr.success(
            enabled
              ? 'Ableitung eingeschaltet — beim nächsten Abgleich werden Lerner zugewiesen.'
              : 'Ableitung ausgeschaltet — abgeleitete Zuweisungen fallen beim nächsten Abgleich weg.',
            'Gespeichert'
          );
          this.loadDerivable();
        },
        error: () => this.toastr.danger('Speichern fehlgeschlagen', 'Zuweisung'),
      });
  }

  deriveNow() {
    if (!this.selectedProgramId) {
      this.toastr.warning(
        'Wähle links einen Studiengang — die Ableitung gilt immer für einen Studiengang.',
        'Zuweisung'
      );
      return;
    }
    this.derivePending = true;
    this.curriculumService.deriveEnrollments(this.selectedProgramId).subscribe({
      next: (r) => {
        this.derivePending = false;
        if (!r.enabled) {
          this.toastr.warning(
            'Die Ableitung ist für diesen Studiengang ausgeschaltet — nichts geändert.',
            'Zuweisung'
          );
          return;
        }
        this.toastr.success(
          `${r.target} Lerner abgeleitet — ${r.added} neu, ${r.updated} aktualisiert, ${r.removed} entfernt`,
          'Abgeglichen'
        );
        this.loadDerivable();
      },
      error: () => {
        this.derivePending = false;
        this.toastr.danger('Abgleich fehlgeschlagen', 'Zuweisung');
      },
    });
  }

  submitProgram() {
    if (this.programForm.invalid) {
      this.programForm.markAllAsTouched();
      return;
    }
    this.curriculumService
      .createProgram(this.programForm.value as any)
      .subscribe({
        next: (p) => {
          this.toastr.success('Studiengang angelegt', p.name);
          this.programForm.reset({ name: '', code: '', description: '' });
          this.createMode = null;
          this.closeCreateDialog();
          this.selectProgram(p.id);
        },
        error: () => this.toastr.danger('Fehler beim Anlegen', 'Studiengang'),
      });
  }

  deleteProgram(program: StudyProgram) {
    this.confirmThen(`Studiengang "${program.name}" wirklich löschen?`, () => {
      this.curriculumService.deleteProgram(program.id).subscribe({
        next: () => {
          if (this.selectedProgramId === program.id) {
            this.selectedProgramId = null;
          }
          this.toastr.success('Gelöscht', program.name);
        },
        error: () => this.toastr.danger('Fehler beim Löschen', 'Studiengang'),
      });
    });
  }

  addGoal() {
    this.learningGoals.push(
      this.fb.group({
        id: [''],
        description: ['', Validators.required],
        bloom_level: ['understand'],
        status: ['default'],
      })
    );
  }

  removeGoal(i: number) {
    this.learningGoals.removeAt(i);
  }

  submitItem() {
    if (!this.selectedProgramId || this.itemForm.invalid) {
      this.itemForm.markAllAsTouched();
      return;
    }
    const payload: CurriculumItemInput = {
      course_id: this.itemForm.value.course_id,
      semester: this.itemForm.value.semester,
      is_mandatory: this.itemForm.value.is_mandatory,
      learning_goals: this.itemForm.value.learning_goals as LearningGoal[],
    };
    this.curriculumService
      .createItem(this.selectedProgramId, payload)
      .subscribe({
        next: () => {
          this.toastr.success('Kurs zum Curriculum hinzugefügt', '');
          this.itemForm.reset({
            course_id: '',
            semester: 1,
            is_mandatory: true,
          });
          while (this.learningGoals.length) {
            this.learningGoals.removeAt(0);
          }
        },
        error: () => this.toastr.danger('Fehler beim Hinzufügen', 'Curriculum'),
      });
  }

  deleteItem(item: CurriculumItem) {
    this.confirmThen('Curriculum-Eintrag wirklich löschen?', () => {
      this.curriculumService.deleteItem(item.id).subscribe({
        next: () => this.toastr.success('Gelöscht', 'Curriculum-Eintrag'),
        error: () => this.toastr.danger('Fehler beim Löschen', 'Curriculum'),
      });
    });
  }

  courseName(course_id: string): string {
    for (const cat of this.moodleCourses) {
      const m = cat.courses.find((c) => c.course_id === course_id);
      if (m) return m.name;
    }
    return course_id;
  }
}
